import { Box, Skeleton } from '@mui/material';
import { tokens } from '../../theme';

/**
 * The rail's shape in grey: five stage bars over their rows of chips.
 *
 * **Drawn as the rail rather than as one block**, because the rail is the widest
 * thing on the page and a single grey slab where it goes reads as a broken
 * banner rather than as navigation arriving. It also keeps the height exactly
 * right, so nothing below it moves when the phases land.
 *
 * **The stage sizes are hardcoded, and that is the point.** The taxonomy is
 * eleven phases across five stages and has been for as long as this catalog has
 * existed; a skeleton derived from data it does not have yet could only ever be
 * a guess at a different shape. If the taxonomy changes this is a place that
 * needs changing with it — which is a smaller cost than a placeholder that
 * never matches.
 */
const STAGES = ['delivery', 'discover', 'design', 'build', 'run'] as const;

/** How many phase chips sit under each of them. */
const CHIP_COUNTS: Record<(typeof STAGES)[number], number> = {
  delivery: 1,
  discover: 1,
  design: 3,
  build: 3,
  run: 3,
};

export const LifecycleRailSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 2, width: '100%' }} aria-hidden>
    {STAGES.map((stage) => (
      <Box
        key={stage}
        sx={{
          // The same proportional growth the real rail uses, so the bars land
          // at the widths they are about to have.
          flexGrow: CHIP_COUNTS[stage],
          flexBasis: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Skeleton
          variant="rounded"
          height={28}
          sx={{ borderRadius: `${String(tokens.radius.md)}px` }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          {Array.from({ length: CHIP_COUNTS[stage] }, (_, index) => (
            <Skeleton
              key={`${stage}-${String(index)}`}
              variant="rounded"
              height={24}
              sx={{ flex: 1, borderRadius: `${String(tokens.radius.full)}px` }}
            />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);
